import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "hidden", "container"]
  static values = {
    validator: String,     // Name of validation function to use
    errorMessage: String,  // Custom error message template
    tagClass: String,      // CSS classes for tag elements
    allowDuplicates: Boolean
  }

  connect() {
    this.tags = new Set(
      this.hiddenTarget.value
        .split(",")
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0)
    )
    this.renderTags()
  }

  renderTags() {
    this.containerTarget.innerHTML = ""
    this.hiddenTarget.value = Array.from(this.tags).join(",")

    Array.from(this.tags).forEach(tag => {
      const tagElement = document.createElement("span")
      tagElement.className = this.tagClassValue || "inline-flex items-center px-2 py-1 rounded-md text-sm font-medium bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-300"

      const textSpan = document.createElement("span")
      textSpan.textContent = tag
      tagElement.appendChild(textSpan)

      const removeButton = document.createElement("button")
      removeButton.type = "button"
      removeButton.className = "ml-1 inline-flex items-center p-0.5 text-primary-400 hover:text-primary-900 dark:hover:text-primary-200"
      removeButton.innerHTML = `
        <svg class="w-3 h-3" aria-hidden="true" fill="currentColor" viewBox="0 0 20 20">
          <path d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"></path>
        </svg>
      `
      removeButton.addEventListener("click", () => this.removeTag(tag))
      tagElement.appendChild(removeButton)

      this.containerTarget.appendChild(tagElement)
    })
  }

  handleKeydown(event) {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault()
      const value = this.inputTarget.value.trim()

      if (!value) return

      if (this.validateTag(value)) {
        if (this.allowDuplicatesValue || !this.tags.has(value)) {
          this.addTag(value)
          this.inputTarget.value = ""
        } else {
          this.showError(`"${value}" has already been added`)
        }
      } else {
        this.showError(this.formatErrorMessage(value))
      }
    } else if (event.key === "Backspace" && !this.inputTarget.value) {
      const tags = Array.from(this.tags)
      if (tags.length > 0) {
        this.removeTag(tags[tags.length - 1])
      }
    }
  }

  validateTag(value) {
    // If no validator specified, accept all values
    if (!this.validatorValue) return true

    // Use built-in validators or return true if validator not found
    return this.validators[this.validatorValue]?.(value) ?? true
  }

  // Built-in validators
  validators = {
    ipOrCidr: (value) => {
      // IPv4 address or CIDR
      const ipv4Pattern = /^(\d{1,3}\.){3}\d{1,3}(\/([0-9]|[1-2][0-9]|3[0-2]))?$/
      if (ipv4Pattern.test(value)) {
        // Validate each octet
        const octets = value.split('/')[0].split('.')
        return octets.every(octet => parseInt(octet) <= 255)
      }

      // IPv6 address or CIDR
      const ipv6Pattern = /^([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}(\/([0-9]|[1-9][0-9]|1[0-1][0-9]|12[0-8]))?$|^(([0-9a-fA-F]{1,4}:){0,6}[0-9a-fA-F]{1,4})?::([0-9a-fA-F]{1,4}:){0,6}[0-9a-fA-F]{1,4}(\/([0-9]|[1-9][0-9]|1[0-1][0-9]|12[0-8]))?$/
      return ipv6Pattern.test(value)
    },
    
    email: (value) => {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
    },

    // Add more validators as needed
  }

  formatErrorMessage(value) {
    if (this.hasErrorMessageValue) {
      return this.errorMessageValue.replace('{value}', value)
    }

    // Default error messages based on validator
    const messages = {
      ipOrCidr: `"${value}" is not a valid IP address or CIDR range`,
      email: `"${value}" is not a valid email address`
    }
    return messages[this.validatorValue] || `"${value}" is not valid`
  }

  showError(message) {
    const errorDiv = document.createElement("div")
    errorDiv.className = "text-sm text-red-600 dark:text-red-500 mt-1"
    errorDiv.textContent = message

    this.inputTarget.parentNode.insertBefore(errorDiv, this.inputTarget.nextSibling)

    setTimeout(() => errorDiv.remove(), 3000)
  }

  addTag(tag) {
    this.tags.add(tag)
    this.renderTags()
  }

  removeTag(tag) {
    this.tags.delete(tag)
    this.renderTags()
  }
}